import { Path } from 'constants/router'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useDelayedFlag } from './useDelayedFlag'
import { useEffect, useMemo } from 'react'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspaceRoles } from './useWorkspaceRoles'

/**
 * How long the redirect waits for the workspace selection before giving up on it. The wait exists to
 * read the user's role, so overshooting only delays the redirect — never taking it is the failure
 * that matters, and two `UserDataContext` branches never resolve (see below).
 */
const WORKSPACE_RESOLUTION_TIMEOUT_MS = 5000

/** Where a legacy `/subscription/*` deep link resolves to. */
export interface PricingRedirectTarget {
  /** Route to redirect to, or null while the selected workspace is still resolving. */
  path: typeof Path.WORKSPACE_SETTINGS_PLAN | typeof Path.WORKSPACE_CREATION | null
  /** The user has a workspace but cannot buy a plan in it, so the creation page explains why they are there. */
  isMissingPlanPermission: boolean
}

/**
 * Destination for visitors arriving from bkbn.com/pricing, which still links to the legacy
 * `/subscription/*` steps. Admins buy their plan in the workspace they are already in; everyone else
 * has no plan permission there and gets their own workspace instead (QA-246).
 *
 * Call this only from a component mounted on those routes — it runs a timer and reports to Sentry.
 */
export const usePricingRedirectTarget = (): PricingRedirectTarget => {
  const { isAuthenticated, roles } = useAuth0()
  const { currentUserWorkspace, isWorkspaceResolved } = useUserData()
  const { isWorkspaceAdmin, isWorkspaceSponsored, isRestrictedMember } = useWorkspaceRoles()

  // The selected workspace resolves in an effect, so the first render always reports "no workspace".
  // Only clients ever get a resolution though — waiting on anyone else would hang the redirect on a
  // loading page instead of letting the route fall through to the login screen.
  const awaitsWorkspace = isAuthenticated && roles.isClient && !isWorkspaceResolved

  // ...and not every client resolves either: UserDataContext leaves `isWorkspaceResolved` false for
  // good when a stored workspace is a still-propagating invitation (that branch starts no poll and
  // userMe never refetches on its own), and whenever `clientData` is absent — which needs more than
  // the `roles.isClient` tested above. Both would park this route on a permanent spinner, so the wait
  // is bounded and falls through to the pre-QA-246 destination.
  const resolutionTimedOut = useDelayedFlag(awaitsWorkspace, WORKSPACE_RESOLUTION_TIMEOUT_MS)

  useEffect(() => {
    if (!resolutionTimedOut) return

    logger.warn('Pricing redirect timed out waiting for the workspace selection', undefined, {
      context: {
        component: 'usePricingRedirectTarget',
        action: 'workspaceResolutionTimeout',
        metadata: { hasWorkspace: !!currentUserWorkspace },
      },
    })
  }, [resolutionTimedOut, currentUserWorkspace])

  return useMemo(() => {
    if (awaitsWorkspace && !resolutionTimedOut) return { path: null, isMissingPlanPermission: false }

    // Mirrors the three gates in front of the Plan page (WorkspacePageController): workspace settings
    // bounce sponsored and restricted members outright, and Plan is admin-only. Sending someone the
    // Plan page rejects would drop them on the dashboard with no explanation.
    const canManagePlan = !!currentUserWorkspace && isWorkspaceAdmin && !isWorkspaceSponsored && !isRestrictedMember
    if (canManagePlan) return { path: Path.WORKSPACE_SETTINGS_PLAN, isMissingPlanPermission: false }

    return { path: Path.WORKSPACE_CREATION, isMissingPlanPermission: !!currentUserWorkspace }
  }, [awaitsWorkspace, resolutionTimedOut, currentUserWorkspace, isWorkspaceAdmin, isWorkspaceSponsored, isRestrictedMember])
}
